// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHotelPartnerId } from './useHotelPartnerId';
import { blockTotals } from '../roomBlockUtils';

export interface HotelPropertyFull {
  id: string;
  event_id: string;
  hotel_assignment_id: string | null;
  hotel_name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  distance_miles: number | null;
  group_rate_per_night: number | null;
  rate_valid_until: string | null;
  booking_link: string | null;
  star_rating: number | null;
  amenities: string[] | null;
  photo_url: string | null;
  is_primary: boolean;
  rooms_contracted: number | null;
  rooms_released_date: string | null;
  comp_room_ratio: number | null;
  attrition_pct: number | null;
  attrition_penalty_note: string | null;
  rebate_per_room: number | null;
  commission_pct: number | null;
  hotel_contact_name: string | null;
  hotel_contact_email: string | null;
  hotel_contact_phone: string | null;
  rfp_status: string | null;
  rooms_booked: number;
  block_rooms_held: number;
  block_rooms_reserved: number;
}

export interface RfpRequest {
  id: string;
  event_id: string;
  hotel_name: string;
  hotel_address: string | null;
  hotel_city: string | null;
  hotel_state: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  rooms_requested: number | null;
  check_in_date: string | null;
  check_out_date: string | null;
  desired_rate: number | null;
  amenities_required: string[] | null;
  notes: string | null;
  status: string;
  responded_at: string | null;
  offered_rate: number | null;
  converted_to_property_id: string | null;
}

export function useHotelPropertiesFull(eventId: string | undefined) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['hotels-full', eventId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_properties')
        .select('*')
        .eq('event_id', eventId!)
        .order('is_primary', { ascending: false })
        .order('hotel_name');
      if (error) throw error;

      const hotels = data ?? [];
      const hotelIds = hotels.map((h) => h.id);

      // Get booked rooms per hotel
      const bookingMap = new Map<string, number>();
      if (hotelIds.length > 0) {
        const { data: bookings } = await (supabase as any)
          .from('hotel_bookings')
          .select('hotel_property_id, number_of_rooms')
          .in('hotel_property_id', hotelIds)
          .in('status', ['confirmed', 'pending']);
        if (bookings) {
          for (const b of bookings) {
            if (b.hotel_property_id) {
              bookingMap.set(b.hotel_property_id, (bookingMap.get(b.hotel_property_id) ?? 0) + (b.number_of_rooms ?? 0));
            }
          }
        }
      }

      const { data: blocks, error: blockError } = await (supabase as any).from('hotel_room_blocks')
        .select('hotel_property_id, rooms_held, rooms_reserved').eq('event_id', eventId);
      if (blockError) throw blockError;
      return hotels.map((h): HotelPropertyFull => ({
        ...h,
        is_primary: h.is_primary ?? false,
        rooms_booked: bookingMap.get(h.id) ?? 0,
        block_rooms_held: blockTotals(blocks || [], h.id).held,
        block_rooms_reserved: blockTotals(blocks || [], h.id).reserved,
      }));
    },
    enabled: !!eventId && !!partnerId,
  });
}

export function useSaveHotelProperty(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data, makePrimary }: { id?: string; data: Record<string, unknown>; makePrimary?: boolean }) => {
      if (makePrimary && eventId) {
        await (supabase as any).from('hotel_properties').update({ is_primary: false }).eq('event_id', eventId);
      }
      if (id) {
        const { error } = await (supabase as any).from('hotel_properties').update(data).eq('id', id);
        if (error) throw error;
        return id;
      } else {
        const { data: inserted, error } = await (supabase as any).from('hotel_properties').insert(data).select('id').single();
        if (error) throw error;
        return inserted.id;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotels-full', eventId] });
    },
  });
}

export function useDeleteHotelProperty(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('hotel_properties').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotels-full', eventId] });
    },
  });
}

// RFP hooks
export function useRfpRequests(eventId: string | undefined) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['hotel-rfps', eventId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_rfp_requests')
        .select('*')
        .eq('event_id', eventId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as RfpRequest[];
    },
    enabled: !!eventId && !!partnerId,
  });
}

export function useSaveRfp(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: Record<string, unknown> }) => {
      if (id) {
        const { error } = await (supabase as any).from('hotel_rfp_requests').update(data).eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any).from('hotel_rfp_requests').insert(data);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-rfps', eventId] });
    },
  });
}

export function useConvertRfpToProperty(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rfpId, propertyId }: { rfpId: string; propertyId: string }) => {
      const { error } = await (supabase as any)
        .from('hotel_rfp_requests')
        .update({ converted_to_property_id: propertyId, status: 'accepted' })
        .eq('id', rfpId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-rfps', eventId] });
      qc.invalidateQueries({ queryKey: ['hotels-full', eventId] });
    },
  });
}
